import { Fragment, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box } from '@mui/material';

import PoweredBy from '../ui/poweredBy';
import { getEventSectionsLeaderboard } from '../../features/leaderboard/leaderboardSlice';
import BSCircularProgress from '../ui/progress/circularProgress';
import SectionTotalsCard from '../ui/scoreboard/sectionTotalsCard';
import { useParams } from 'react-router-dom';

import { doc, onSnapshot } from 'firebase/firestore';
import { store } from '../../config/firebase';
import { useColorMode } from '../../hooks/colorModeProvider';
import { setEvent, setLoadingEvent } from '../../features/event/eventSlice';

const ForTVLogs = () => {
  const dispatch = useDispatch();
  const { eventId } = useParams();
  const [scrollDirection, setScrollDirection] = useState(1); // 1 for down, -1 for up

  const event = useSelector((state) => state.event.event);
  const loadingEvent = useSelector((state) => state.event.loadingEvent);
  const { mode, setMode } = useColorMode();

  const loadingLeaderboard = useSelector((state) => state.leaderboard.loading);
  const sectionsLeaderboard = useSelector((state) => state.leaderboard.sectionLeaderboard);

  useEffect(() => {
    if (!eventId) return;

    dispatch(setLoadingEvent(true));
    const docRef = doc(store, 'events', eventId);

    const unsubscribe = onSnapshot(docRef, (docSnap) => {
      if (docSnap.exists()) {
        const eventData = { id: docSnap.id, ...docSnap.data() };
        dispatch(setEvent(eventData));
      } else {
        console.log('No such document!');
      }
      dispatch(setLoadingEvent(false));
    });

    return () => unsubscribe();
  }, [eventId, dispatch]);

  useEffect(() => {
    setMode('light');
  }, [mode, setMode]);

  useEffect(() => {
    if (event) {
      dispatch(getEventSectionsLeaderboard({ eventId: event.id }));
    }
  }, [dispatch, event]);

  // Auto-scroll the entire page
  useEffect(() => {
    if (!sectionsLeaderboard || sectionsLeaderboard.length === 0) return;

    const scrollSpeed = 1; // pixels per frame
    const pauseDuration = 3000; // pause at top/bottom for 3 seconds
    let isPaused = false;
    let pauseTimeout = null;

    const scroll = () => {
      if (isPaused) return;

      const { scrollY } = window;
      const { scrollHeight, clientHeight } = document.documentElement;
      const maxScroll = scrollHeight - clientHeight;

      if (maxScroll <= 0) return; // No need to scroll if content fits

      // Check if we've reached the bottom
      if (scrollY >= maxScroll && scrollDirection === 1) {
        isPaused = true;
        setScrollDirection(-1);
        pauseTimeout = setTimeout(() => {
          isPaused = false;
        }, pauseDuration);
        return;
      }

      // Check if we've reached the top
      if (scrollY <= 0 && scrollDirection === -1) {
        isPaused = true;
        setScrollDirection(1);

        // Refresh leaderboard when reaching the top
        if (event) {
          dispatch(getEventSectionsLeaderboard({ eventId: event.id }));
        }

        pauseTimeout = setTimeout(() => {
          isPaused = false;
        }, pauseDuration);
        return;
      }

      // Scroll smoothly
      window.scrollBy(0, scrollSpeed * scrollDirection);
    };

    const interval = setInterval(scroll, 16); // ~60fps

    return () => {
      clearInterval(interval);
      if (pauseTimeout) clearTimeout(pauseTimeout);
    };
  }, [sectionsLeaderboard, scrollDirection, dispatch, event]);

  return !event ? null : (
    <Box
      display={'flex'}
      flexDirection={'column'}
      alignItems={'center'}
      sx={{
        transform: 'scale(1.7)',
        transformOrigin: 'top center',
        width: '58.82%', // Compensate for the 170% scale (100/1.7)
        margin: '0 auto'
      }}>
      <PoweredBy />
      {loadingLeaderboard ? (
        <BSCircularProgress caption={'Loading...'} size={30} />
      ) : (
        sectionsLeaderboard && (
          <Box
            sx={{ width: '100%' }}
            display={'flex'}
            flexDirection={'row'}
            gap={3}
            justifyContent={'center'}
            flexWrap={'wrap'}>
            {sectionsLeaderboard.map((section, index) => (
              <Fragment key={index}>
                <SectionTotalsCard section={section} />
              </Fragment>
            ))}
          </Box>
        )
      )}
    </Box>
  );
};

export default ForTVLogs;
